import { motion } from "framer-motion";
import { ArrowRight, Lock } from "@phosphor-icons/react";
import HeroCanvas from "@/components/HeroCanvas";
import { openStrategySession } from "@/lib/calcom";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#050505]"
    >
      <HeroCanvas />
      <div className="absolute inset-0 precision-grid opacity-40 pointer-events-none" />
      <Brackets />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 pt-32 md:pt-40 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-10"
          data-testid="hero-overline"
        >
          <span className="h-px w-10 bg-[#CCFF00]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">
            Your pipeline. Built once. Runs forever.
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-bold text-white tracking-[-0.04em] leading-[0.94] text-[40px] sm:text-[60px] md:text-[78px] lg:text-[96px] uppercase max-w-[17ch]"
          data-testid="hero-headline"
        >
          GTM &amp; RevOps
          <br />
          Infrastructure
          <br />
          Engineered to Scale
          <br />
          Your Pipeline<span className="caret text-[#CCFF00]">_</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-start"
        >
          <div className="md:col-span-7 lg:col-span-6">
            <p className="font-body text-zinc-300 text-[15px] md:text-[17px] leading-relaxed max-w-[60ch]" data-testid="hero-sub">
              A fully autonomous pipeline system that delivers qualified buyers showing
              active buying signals directly to your reps <span className="text-white">every week</span>.
              No setup. No extra headcount.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="hero-cta-row">
              <button
                onClick={openStrategySession}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#CCFF00] text-black font-mono font-bold uppercase tracking-[0.15em] text-[12px] px-6 py-4 hover:bg-white transition-colors"
              >
                Book a Strategy Session3
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </button>

              <a
                href="#architecture"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-white/70 text-white font-mono font-medium uppercase tracking-[0.15em] text-[12px] px-6 py-4 transition-colors"
              >
                See The Architecture
              </a>
            </div>

            <p className="mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              <Lock size={12} weight="bold" className="text-[#D946EF]" />
              If we don&apos;t deliver — you don&apos;t pay.
            </p>
          </div>

          <div className="md:col-span-5 lg:col-span-5 lg:col-start-8">
            <div className="glass p-7 relative" data-testid="hero-principles">
              <div className="absolute -top-2 -left-2 w-3 h-3 border-l border-t border-[#00F0FF]" />
              <div className="absolute -top-2 -right-2 w-3 h-3 border-r border-t border-[#00F0FF]" />
              <div className="absolute -bottom-2 -left-2 w-3 h-3 border-l border-b border-[#00F0FF]" />
              <div className="absolute -bottom-2 -right-2 w-3 h-3 border-r border-b border-[#00F0FF]" />

              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
                    // how every engagement works
                  </div>
                  <div className="font-display font-bold text-white text-2xl md:text-[28px] uppercase tracking-tighter leading-[0.95]">
                    How we work.<br />Every time.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <PrincipleRow
                  n="01"
                  accent="#00F0FF"
                  title="Systems outlast teams."
                  desc="Built to operate without hand-holding. The more it runs, the sharper it gets."
                />
                <PrincipleRow
                  n="02"
                  accent="#FFB800"
                  title="No black boxes."
                  desc="Every signal caught, every trigger fired, every action logged. Full visibility into what the system is doing, in real time."
                />
                <PrincipleRow
                  n="03"
                  accent="#D946EF"
                  title="Pipeline is the only scorecard."
                  desc="Measured on SQLs routed to your reps — not reports, not strategy sessions, not activity."
                />
                <PrincipleRow
                  n="04"
                  accent="#CCFF00"
                  title="While they hire, you scale."
                  desc="While your competitors are onboarding SDRs, your system is already finding better accounts, firing faster, and compounding weekly."
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/60 backdrop-blur-sm overflow-hidden">
        <div className="flex items-center gap-8 py-3 marquee-track whitespace-nowrap w-max font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
          {Array.from({ length: 3 }).map((_, rep) => (
            <div key={rep} className="flex items-center gap-8">
              <TickerItem dot="#00F0FF" text="Data foundation → stable" />
              <TickerItem dot="#007AFF" text="TAM enrichment · 98.2%" />
              <TickerItem dot="#FFB800" text="Signal capture online" />
              <TickerItem dot="#D946EF" text="Activation engine · warm" />
              <TickerItem dot="#CCFF00" text="Agents deployed · 7" />
              <TickerItem dot="#ffffff" text="CRM sync · 212ms" />
              <TickerItem dot="#00F0FF" text="Pipeline cadence · weekly" />
              <TickerItem dot="#FFB800" text="Risk reversal · active" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PrincipleRow({ n, accent, title, desc }) {
  return (
    <div className="flex items-start gap-4 group">
      <span className="font-mono text-[11px] font-bold tabular-nums shrink-0 pt-0.5" style={{ color: accent }}>
        {n}
      </span>
      <div className="flex-1 border-b border-dashed border-white/10 pb-4 group-last:border-0 group-last:pb-0">
        <div className="font-display font-bold text-white text-[15px] uppercase tracking-tight leading-none">
          {title}
        </div>
        <div className="mt-1.5 font-body text-zinc-400 text-[13px] leading-relaxed">
          {desc}
        </div>
      </div>
      <span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
    </div>
  );
}

function TickerItem({ dot, text }) {
  return (
    <span className="flex items-center gap-3">
      <span className="h-1 w-1 rounded-full" style={{ background: dot, boxShadow: `0 0 6px ${dot}` }} />
      {text}
      <span className="text-zinc-700">+</span>
    </span>
  );
}

function Brackets() {
  const cls = "absolute w-6 h-6 border-white/30";
  return (
    <>
      <div className={`${cls} top-24 left-6 border-l border-t`} />
      <div className={`${cls} top-24 right-6 border-r border-t`} />
      <div className={`${cls} bottom-16 left-6 border-l border-b`} />
      <div className={`${cls} bottom-16 right-6 border-r border-b`} />
    </>
  );
}
